import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { bulkSetDependencies } from "../../Redux/actions/questionAction";
import { createNewDependency } from "../../utils/question";
import {
  ModalActionButtonStyles,
  ModalBaseStyles,
  ModalHeader,
} from "../Common/styles/modal";
import DependencyConditionFields from "./QuestionProperties/DependencyConditionFields";
import DependencyParentPicker from "./QuestionProperties/DependencyParentPicker";
import {
  getOrderingWarning,
  hasExistingDependency,
  validateBulkSelection,
} from "./QuestionProperties/bulkDependencyUtils";
import { useDependencyParentPicker } from "./QuestionProperties/useDependencyParentPicker";

type Props = {
  closeModal: () => void;
};

type DependencyState = {
  parentFormId: string;
  parentQuestionId: string;
  operator: string;
  textValue?: string;
  numberValue?: number;
  questionChoiceId?: string;
};

const BulkDependencyModal = ({ closeModal }: Props) => {
  const dispatch = useAppDispatch();

  const questions = useAppSelector((state) => state.question.questions);
  const studyId = useAppSelector((state) => state.question.studyId);
  const formId = useAppSelector((state) => state.question.formId);
  const formCategory = useAppSelector((state) => state.question.formCategory);
  const formLevel = useAppSelector((state) => state.question.formLevel);

  const [dependency, setDependency] = useState<DependencyState>(() =>
    createNewDependency(formCategory, formId)
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const { isLoading, formList, selectedQuestion } = useDependencyParentPicker({
    parentFormId: dependency.parentFormId,
    parentQuestionId: dependency.parentQuestionId,
    studyId,
    formCategory,
    formLevel,
    currentFormId: formId,
  });

  const hasControlQuestion = !!dependency.parentQuestionId;

  useEffect(() => {
    if (!dependency.parentQuestionId) {
      setSelectedIds([]);
      return;
    }

    setSelectedIds(
      questions
        .filter(
          (q) =>
            q.id !== dependency.parentQuestionId && !hasExistingDependency(q)
        )
        .map((q) => q.id)
    );
  }, [dependency.parentQuestionId, questions]);

  const handleDependencyFieldChange = (key: string, value: string | number) => {
    setDependency((prev) => {
      const next = { ...prev };

      if (key === "parentFormId") {
        next.parentQuestionId = "";
        next.operator = "";
        delete next.textValue;
        delete next.numberValue;
        delete next.questionChoiceId;
        next.parentFormId = value as string;
        return next;
      }

      if (key === "parentQuestionId") {
        next.operator = "";
        delete next.textValue;
        delete next.numberValue;
        delete next.questionChoiceId;
        next.parentQuestionId = value as string;
        return next;
      }

      if (key === "textValue") {
        delete next.numberValue;
        delete next.questionChoiceId;
        next.textValue = value as string;
      } else if (key === "numberValue") {
        delete next.textValue;
        delete next.questionChoiceId;
        const num = parseFloat(value as string);
        next.numberValue = isNaN(num) ? undefined : num;
      } else if (key === "questionChoiceId") {
        delete next.textValue;
        delete next.numberValue;
        next.questionChoiceId = value as string;
      } else if (key === "operator") {
        next.operator = value as string;
      }

      return next;
    });
  };

  const { blockers } = useMemo(
    () =>
      validateBulkSelection({
        selectedIds,
        parentQuestionId: dependency.parentQuestionId,
        parentQuestion: selectedQuestion,
        questions,
        dependency,
        formId,
        formList,
      }),
    [selectedIds, dependency, selectedQuestion, questions, formId, formList]
  );

  const selectableQuestions = questions.filter(
    (q) => q.id !== dependency.parentQuestionId
  );

  const allSelectableSelected =
    selectableQuestions.length > 0 &&
    selectableQuestions.every((q) => selectedIds.includes(q.id));

  const toggleQuestion = (questionId: string) => {
    setSelectedIds((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  const toggleSelectAll = () => {
    const allIds = selectableQuestions.map((q) => q.id);
    if (allSelectableSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allIds);
    }
  };

  const submitHandler = async () => {
    if (blockers.length > 0 || !selectedIds.length) {
      return;
    }

    try {
      setSubmitting(true);
      await dispatch(
        bulkSetDependencies({
          questionIds: selectedIds,
          dependency: {
            parentFormId: dependency.parentFormId,
            parentQuestionId: dependency.parentQuestionId,
            operator: dependency.operator,
            textValue: dependency.textValue,
            numberValue: dependency.numberValue,
            questionChoiceId: dependency.questionChoiceId,
          },
        })
      );
      closeModal();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={closeModal}>
      <Box
        sx={{
          ...ModalBaseStyles,
          minHeight: "20vh",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
      <ModalHeader
        title="Set dependencies for multiple fields"
        onCloseClick={closeModal}
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Apply one condition to many fields at once.
      </Typography>

      <Stack gap={2} sx={{ overflowY: "auto", flex: 1, pr: 0.5 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Show the fields when this question...
        </Typography>

        {isLoading ? (
          <Stack alignItems="center" py={2}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <>
            <DependencyParentPicker
              parentFormId={dependency.parentFormId}
              parentQuestionId={dependency.parentQuestionId}
              studyId={studyId}
              formCategory={formCategory}
              formLevel={formLevel}
              currentFormId={formId}
              showFormPicker={
                formCategory === "visit" || formCategory === "survey"
              }
              onParentFormChange={(formIdValue) =>
                handleDependencyFieldChange("parentFormId", formIdValue)
              }
              onParentQuestionChange={(questionId) =>
                handleDependencyFieldChange("parentQuestionId", questionId)
              }
            />

            <DependencyConditionFields
              dependency={dependency}
              selectedQuestion={selectedQuestion}
              onChange={handleDependencyFieldChange}
              onDateChange={(dateValue) =>
                handleDependencyFieldChange("textValue", dateValue)
              }
            />
          </>
        )}

        {hasControlQuestion && (
        <Stack gap={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={600}>
              Apply to these fields
            </Typography>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                {selectedIds.length} selected
              </Typography>
              <Button size="small" onClick={toggleSelectAll}>
                {allSelectableSelected ? "Deselect all" : "Select all"}
              </Button>
            </Stack>
          </Stack>

          <Stack gap={0.5}>
            {questions.map((question) => {
              const isControlling = question.id === dependency.parentQuestionId;
              const overwrite = hasExistingDependency(question);
              const orderingWarning =
                selectedQuestion && !isControlling
                  ? getOrderingWarning(
                      { ...question, formId },
                      selectedQuestion,
                      formId,
                      formList,
                      dependency.parentFormId
                    )
                  : null;

              return (
                <Box
                  key={question.id}
                  sx={{
                    border: orderingWarning
                      ? "1px solid"
                      : "1px solid transparent",
                    borderColor: orderingWarning ? "warning.main" : "transparent",
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  <FormControlLabel
                    disabled={isControlling}
                    control={
                      <Checkbox
                        checked={
                          isControlling
                            ? false
                            : selectedIds.includes(question.id)
                        }
                        onChange={() => toggleQuestion(question.id)}
                      />
                    }
                    label={
                      <Stack direction="row" alignItems="center" gap={1}>
                        <Typography variant="body2">
                          {question.varname} —{" "}
                          {question.label.length > 60
                            ? question.label.slice(0, 60) + "..."
                            : question.label}
                        </Typography>
                        {overwrite && !isControlling && (
                          <Chip
                            size="small"
                            label="will overwrite"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        {isControlling && (
                          <Chip
                            size="small"
                            label="controlling question"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                    }
                  />
                  {orderingWarning && (
                    <Typography
                      variant="caption"
                      color="warning.main"
                      sx={{ pl: 4 }}
                    >
                      {orderingWarning}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Stack>
        )}
      </Stack>

      <Box sx={{ mt: 2 }}>
        {blockers.map((message) => (
          <Typography
            key={message}
            variant="caption"
            color="error"
            display="block"
            mb={0.5}
          >
            {message}
          </Typography>
        ))}
        {hasControlQuestion && (
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Applied together — all fields or none.
          </Typography>
        )}
        <Box sx={ModalActionButtonStyles}>
          <Button variant="outlined" onClick={closeModal} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitHandler}
            disabled={
              submitting ||
              !hasControlQuestion ||
              blockers.length > 0 ||
              !selectedIds.length
            }
          >
            {submitting ? (
              <CircularProgress size={22} />
            ) : hasControlQuestion ? (
              `Apply to ${selectedIds.length} field${selectedIds.length === 1 ? "" : "s"}`
            ) : (
              "Apply"
            )}
          </Button>
        </Box>
      </Box>
      </Box>
    </Modal>
  );
};

export default BulkDependencyModal;
